<script setup lang="ts">
import { onMounted, onScopeDispose, ref, shallowRef } from "vue";
import { errorMessage } from "@/composables/generated/contracts";
import { useUtility } from "@/composables/useUtility";
import type { PersonStaff } from "../../../cbi-generator/generated/dto/PersonStaff";
import type { PersonPhoneStaff } from "../../../cbi-generator/generated/dto/PersonPhoneStaff";
import type { PersonAddressStaff } from "../../../cbi-generator/generated/dto/PersonAddressStaff";
import type { MembershipStaff } from "../../../cbi-generator/generated/dto/MembershipStaff";
import type { MembershipStaffSearchParams } from "../../../cbi-generator/generated/dto/MembershipStaffSearchParams";
import { MembershipStaffSearchParamsMeta } from "../../../cbi-generator/generated/dto/MembershipStaffSearchParamsMeta";
import { PurchasableState } from "../../../cbi-generator/generated/dto/PurchasableState";
import { useMembershipService } from "../../../cbi-generator/frontend/composables/useMembershipFrontendService";
import { emptySearchParams } from "@/composables/useTableHelper";

const props = defineProps<{ person: PersonStaff }>();
const { formatPhoneNumber, formatDateNY, calculateAge } = useUtility();
const membershipService = useMembershipService();
const phones = shallowRef<PersonPhoneStaff[]>([]);
const addresses = shallowRef<PersonAddressStaff[]>([]);
const emergencyContacts = shallowRef<
  { person: PersonStaff; phones: PersonPhoneStaff[] }[]
>([]);
const memberships = ref<string[]>([]);
const latestCard = ref("");
const loading = ref(false);
const error = ref("");
let disposed = false;
onScopeDispose(() => {
  disposed = true;
});
async function loadMemberships(): Promise<MembershipStaff[]> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const params: MembershipStaffSearchParams = {
    ...emptySearchParams<MembershipStaffSearchParams>(
      MembershipStaffSearchParamsMeta,
    ),
    personIdExact: props.person.personId,
    purchasableStateExact: PurchasableState.ACTIVE,
    startDateLessEq: today,
    sort: [],
  };
  const rows: MembershipStaff[] = [];
  let page = 1;
  while (!disposed) {
    const result = await membershipService.searchMembershipStaffFull(
      params,
      page,
      100,
      { personId: false, membershipTypeId: true },
    );
    rows.push(...result.items);
    if (page >= result.totalPages) break;
    page++;
  }
  return rows;
}
async function load() {
  loading.value = true;
  error.value = "";
  try {
    const [loadedPhones, loadedAddresses, contacts, rows, cards] =
      await Promise.all([
        props.person.phonesLazy(),
        props.person.addressesLazy(),
        props.person.getEmergencyContacts(),
        loadMemberships(),
        props.person.cardsLazy(),
      ]);
    const now = Date.now();
    const active = rows.filter(
      (row) =>
        row.voidCloseId == null &&
        row.purchasableState === "ACTIVE" &&
        row.startDate != null &&
        row.startDate.getTime() <= now &&
        (row.expirationDate == null || row.expirationDate.getTime() > now),
    );
    const contactDetails = await Promise.all(
      contacts.map(async (person) => ({
        person,
        phones: await person.phonesLazy(),
      })),
    );
    const labels = active.map((row) => {
      if (!row.membershipTypeId)
        throw new Error(
          "Membership search did not include its requested type.",
        );
      return `${row.membershipTypeId.membershipTypeName ?? "--"} (${row.expirationDate ? `Until: ${formatDateNY(row.expirationDate)}` : "Lifetime"})`;
    });
    if (disposed) return;
    phones.value = loadedPhones;
    addresses.value = loadedAddresses;
    emergencyContacts.value = contactDetails;
    memberships.value = labels;
    latestCard.value =
      cards
        .filter(
          (card) =>
            card.active &&
            card.voidCloseId == null &&
            card.purchasableState === "ACTIVE",
        )
        .sort(
          (a, b) =>
            (b.issueDate?.getTime() ?? 0) - (a.issueDate?.getTime() ?? 0),
        )[0]?.cardNum ?? "";
  } catch (cause) {
    if (!disposed) error.value = errorMessage(cause);
  } finally {
    if (!disposed) loading.value = false;
  }
}
onMounted(load);
</script>

<template>
  <v-progress-linear v-if="loading" indeterminate />
  <v-alert v-if="error" type="error" role="alert">
    {{ error }} <v-btn @click="load">Retry summary</v-btn>
  </v-alert>
  <DescriptionList>
    <DescriptionListItem term="Latest Card">{{
      latestCard || "--"
    }}</DescriptionListItem>
    <DescriptionListItem term="Contact Information">
      <p>{{ person.email }}</p>
      <p v-for="phone in phones" :key="phone.assignId">
        {{ phone.name }}: {{ formatPhoneNumber(phone.phoneId.phoneNumber) }}
        {{ phone.phoneId.phoneType }}
      </p>
    </DescriptionListItem>
    <DescriptionListItem term="Emergency Contact">
      <div v-for="contact in emergencyContacts" :key="contact.person.personId">
        <p>{{ contact.person.name }}</p>
        <p v-if="contact.person.email">{{ contact.person.email }}</p>
        <p v-for="phone in contact.phones" :key="phone.assignId">
          {{ phone.name }}: {{ formatPhoneNumber(phone.phoneId.phoneNumber) }}
          {{ phone.phoneId.phoneType }}
        </p>
      </div>
      <span v-if="!emergencyContacts.length">--</span>
    </DescriptionListItem>
    <DescriptionListItem term="Current Memberships">
      <p v-for="(membership, index) in memberships" :key="index">
        {{ membership }}
      </p>
      <span v-if="!memberships.length">--</span>
    </DescriptionListItem>
    <DescriptionListItem term="Primary Address">
      <p
        v-for="address in addresses.filter(
          (row) => row.assignId === person.primaryAddressId?.assignId,
        )"
        :key="address.assignId"
      >
        {{ address.addressId.full }}
      </p>
    </DescriptionListItem>
    <DescriptionListItem term="Date of Birth">{{
      person.dob
        ? `${formatDateNY(person.dob)} (${calculateAge(person.dob)})`
        : "--"
    }}</DescriptionListItem>
  </DescriptionList>
</template>
